import { inject, Injectable, signal, Signal } from '@angular/core';
import { Level } from '../models';
import { RoomListItem } from '../models';
import { CRYPTO } from '../tokens';
import { roomListItemsMock } from '../shared';

@Injectable({
    providedIn: 'root'
})
export class LevelsService {
    private _levelsSignal = signal<Level[]>([]);

    private _levels: Level[] = [];
    private crypto = inject(CRYPTO);

    public getLevels(): Signal<Level[]> {
        return this._levelsSignal.asReadonly();
    }

    public initLevels(): void {
        const groundLevel: Level = {
            position: 0,
            id: this.crypto.randomUUID(),
            roomsData: [...this.getRandomRoomListItems()]
        };

        this._levels.push(groundLevel);
        this._levelsSignal.set([...this._levels]);
    }

    public addUpperLevel(): void {
        const position = this._levels.length ? Math.max(...this._levels.map(level => level.position)) + 1 : 1;
        const newLevel: Level = { id: this.crypto.randomUUID(), position, roomsData: [] };
        this._levels.unshift(newLevel);
        this._levelsSignal.set([...this._levels]);
    }

    public addLowerLevel(): void {
        const position = this._levels.length ? Math.min(...this._levels.map(level => level.position)) - 1 : -1;
        const newLevel: Level = { id: this.crypto.randomUUID(), position, roomsData: [] };
        this._levels.push(newLevel);
        this._levelsSignal.set([...this._levels]);
    }

    public deleteLevel(levelId: string): void {
        const targetLevel = this._levels.find(level => level.id === levelId);
        if (!targetLevel) return;

        this._levels.splice(this._levels.indexOf(targetLevel), 1);

        const isTargetLevelAnUpperLevel = targetLevel.position >= 0;
        if (isTargetLevelAnUpperLevel) {
            this._levels = this._levels.map(level => {
                if (level.position > targetLevel.position) {
                    level.position -= 1;
                }
                return level;
            });
        } else {
            this._levels = this._levels.map(level => {
                if (level.position < targetLevel.position) {
                    level.position += 1;
                }
                return level;
            });
        }
        this._levelsSignal.set([...this._levels]);
    }

    public addRooms(levelId: string): void {
        const targetLevel = this._levels.find(level => level.id === levelId);
        if (!targetLevel) return;
        targetLevel.roomsData.push(...this.getRandomRoomListItems());
        this._levelsSignal.set([...this._levels]);
    }

    /**
     *  Temporary mock algorithm. To be replaced with real data from api.
     */
    private getRandomRoomListItems(): RoomListItem[] {
        const maxCount = Math.min(5, roomListItemsMock.length);
        if (!maxCount) {
            return [];
        }

        const minCount = Math.min(2, maxCount);
        const itemsCount = Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount;

        const shuffledItems = [...roomListItemsMock].sort(() => Math.random() - 0.5);
        return shuffledItems.slice(0, itemsCount).map(item => ({ ...item }));
    }
}
